import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useRescueContext } from '../../lib/RescueContext';
import logo from '../../assets/fi-for-rescues-logo-white.svg';
import styles from './Sidebar.module.scss';

interface NavItem {
  path: string;
  key: string;
  label: string;
  icon: React.ReactNode;
  section: 'portal' | 'more';
}

const navItems: NavItem[] = [
  {
    path: '/rescue',
    key: 'dashboard',
    label: 'Dashboard',
    section: 'portal',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    ),
  },
  {
    path: '/rescue/chips',
    key: 'chips',
    label: 'Nano Microchips',
    section: 'portal',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2 12h5" />
        <path d="M17 12h5" />
        <path d="M12 2v5" />
        <path d="M12 17v5" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    path: '/rescue/devices',
    key: 'devices',
    label: 'Fi Devices',
    section: 'portal',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2a8 8 0 0 0-8 8c0 5.4 7.05 11.5 7.35 11.76a1 1 0 0 0 1.3 0C13 21.5 20 15.4 20 10a8 8 0 0 0-8-8z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    path: '/rescue/gwa',
    key: 'gwa',
    label: 'Gift with Adoption',
    section: 'portal',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 12v10H4V12" />
        <path d="M2 7h20v5H2z" />
        <path d="M12 22V7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
      </svg>
    ),
  },
  {
    path: '/rescue/integrations',
    key: 'integrations',
    label: 'Integrations',
    section: 'more',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 11a9 9 0 0 1 9 9" />
        <path d="M4 4a16 16 0 0 1 16 16" />
        <circle cx="5" cy="19" r="1" />
      </svg>
    ),
  },
  {
    path: '/rescue/help',
    key: 'help',
    label: 'Help Center',
    section: 'more',
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { orgName, sidebarOpen, setSidebarOpen } = useRescueContext();

  const isActive = (path: string) => {
    if (path === '/rescue') {
      return location.pathname === '/rescue' || location.pathname === '/rescue/';
    }
    return location.pathname.startsWith(path);
  };

  const portalItems = navItems.filter((item) => item.section === 'portal');
  const moreItems = navItems.filter((item) => item.section === 'more');

  return (
    <aside className={`${styles.sidebar} ${sidebarOpen ? styles.open : ''}`}>
      <div className={styles.logo}>
        <Link to="/rescue">
          <img src={logo} alt="Fi for Rescues" />
        </Link>
      </div>

      <div className={styles.org}>
        <div className={styles.orgName}>{orgName}</div>
        <div className={styles.orgType}>Rescue Organization</div>
      </div>

      <nav className={styles.nav}>
        <div className={styles.sectionLabel}>Portal</div>
        {portalItems.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            className={`${styles.link} ${isActive(item.path) ? styles.active : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}

        <div className={styles.sectionLabel}>More</div>
        {moreItems.map((item) => (
          <Link
            key={item.key}
            to={item.path}
            className={`${styles.link} ${isActive(item.path) ? styles.active : ''}`}
            onClick={() => setSidebarOpen(false)}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </nav>

      <div className={styles.footer}>
        <a href="https://fitracking.com" className={styles.link} target="_blank" rel="noopener noreferrer">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
          fitracking.com
        </a>
      </div>
    </aside>
  );
}
